import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listDocuments,
  getDocument,
  updateDocument,
  getRelatedDocuments,
  deleteDocument,
} from '@/services/documentsApi'
import type { Document, ListDocumentsParams } from '@/types'

// Hook for fetching document list with filters
export function useDocuments(params: ListDocumentsParams) {
  return useQuery({
    queryKey: ['documents', params],
    queryFn: () => listDocuments(params),
    staleTime: 30000, // 30 seconds
    enabled: !!params.business_id,
  })
}

// Hook for fetching single document
export function useDocument(id: string | null) {
  return useQuery({
    queryKey: ['document', id],
    queryFn: () => getDocument(id!),
    enabled: !!id,
  })
}

// Hook for fetching related documents
export function useRelatedDocuments(id: string | null) {
  return useQuery({
    queryKey: ['relatedDocuments', id],
    queryFn: async () => {
      const response = await getRelatedDocuments(id!)
      return response.documents
    },
    enabled: !!id,
  })
}

// Hook for updating document
export function useUpdateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Document> }) =>
      updateDocument(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific document query
      queryClient.invalidateQueries({ queryKey: ['document', id] })
      // Invalidate document list to reflect changes
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

// Hook for deleting document (soft delete)
export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteDocument(id),
    onSuccess: (_, id) => {
      // Remove specific document from cache
      queryClient.removeQueries({ queryKey: ['document', id] })
      // Invalidate document list to reflect deletion
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}
